import { useEffect, useState } from 'react';
import { message } from 'antd';

import { queryClient } from 'config/query-client';
import { createJobNumber, updateJobNumber } from 'services/jobs';
import { NewJobNumberCustomError } from 'types';
import { INVOICE_JOBS, JOB_NUMBERS } from 'events/types';

import useTranslation from '../../customHooks/translate';
import CustomDialog from './CustomDialog';
import { Input } from './styled-components/common-components';

const initialValues = {
  jobId: '',
  id: '',
};

interface IProps {
  open: boolean;
  handleClose: () => void;
  jobNumberToUpdate?: any;
}

const AddNewJobNumberForm = ({
  open,
  handleClose,
  jobNumberToUpdate,
}: IProps) => {
  const [jobError, setJobError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [jobNumber, setJobNumber] = useState(initialValues);

  const translation = useTranslation();

  useEffect(() => {
    setJobNumber(jobNumberToUpdate || initialValues);
  }, [jobNumberToUpdate]);

  const setOnValueChange = (event, propName, isSelect = false) => {
    event.persist && event.persist();
    setJobNumber((prevState) => ({
      ...prevState,
      [propName]: isSelect ? event : event.target.value,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    const method = jobNumber.id ? updateJobNumber : createJobNumber;

    method(jobNumber)
      .then(() => {
        message.success(`${translation.messages.successAdded}`);
        queryClient.invalidateQueries({
          queryKey: [INVOICE_JOBS],
          refetchType: 'all',
        });
        queryClient.invalidateQueries({
          queryKey: [JOB_NUMBERS],
          refetchType: 'all',
        });
        setJobNumber(initialValues);
        handleClose();
      })
      .catch((error: NewJobNumberCustomError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const errorMessageJob = error.response?.data.errors?.JobId as
          | string
          | undefined;
        setJobError(errorMessageJob || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSave();
  };

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      buttonTitle={
        jobNumberToUpdate
          ? translation.jobNumbers.UpdateButton
          : translation.jobNumbers.Header
      }
      isLoading={loading}
      title={
        jobNumberToUpdate
          ? translation.jobNumbers.UpdateButton
          : translation.jobNumbers.Header
      }
      onSubmit={handleSave}
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-[12px]">
          <Input
            className="d-block"
            type="text"
            id="jobId"
            maxLength={30}
            minLength={3}
            onChange={(event) => setOnValueChange(event, 'jobId')}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder="123456"
            value={jobNumber.jobId || ''}
            style={{
              borderColor: jobError ? 'red' : '',
            }}
          />

          {jobError && (
            <span className="text-[12px] text-red-600">{jobError}</span>
          )}
        </div>
      </form>
    </CustomDialog>
  );
};

export default AddNewJobNumberForm;
